import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { toast } from "sonner";
import { contractsApi } from "@/lib/api";
import type { Contract, CreateContractWithFile } from "@/types";

interface UsePatchContractOptions {
  onSuccess?: (contract: Contract) => void;
  onError?: (error: Error) => void;
}

interface PatchContractInput {
  id: string;
  updates: Partial<CreateContractWithFile>;
}

export function usePatchContract(options: UsePatchContractOptions = {}) {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isError, error, isSuccess, data, reset } = useMutation({
    mutationFn: ({ id, updates }: PatchContractInput) => contractsApi.patchContract(id, updates),
    onSuccess: (contract) => {
      queryClient.invalidateQueries({ queryKey: ["contracts"] });
      queryClient.invalidateQueries({ queryKey: ["contract", contract.id] });
      options.onSuccess?.(contract);
    },
    onError: (error: Error) => {
      toast.error("Failed to update contract");
      Sentry.captureException(error);
      options.onError?.(error);
    },
  });

  return {
    patchContract: mutate,
    patchContractAsync: mutateAsync,
    isPending,
    isError,
    error,
    isSuccess,
    contract: data,
    reset,
  };
}
